import ThisBound from 'lib/object/this-bound';
import Events from 'lib/events';
import Bus from 'lib/bus';
import gsap, {TimelineLite, TweenLite} from 'gsap';
import {ScrollTrigger} from 'gsap/ScrollTrigger';
import Group from 'lib/animation/scroll-animations/group';
import ResizeObserver from 'resize-observer-polyfill';
import {function_throttle_initial, array_includes_any, node_each, safe_call} from 'lib/utilities';

type TriggerTarget = string | Element;
type TriggerOptions = gsap.plugins.ScrollTriggerInstanceVars;
type TimelineOptions = gsap.TimelineVars;
type AnimationCondition = string | ((animation: Animation) => boolean);
export type Target = string | Element | object;

interface Options {
    scrollTrigger?: TriggerOptions;
    tweenOptions?: gsap.TweenVars;
    observe?: Element[];
}

interface BaseOptions extends gsap.TweenVars {
    scrollTrigger?: TriggerOptions;
    observe?: HTMLElement[];
}

export interface AnimationOptions extends BaseOptions {
    condition?: AnimationCondition;
}

// Fix missing definitions in @types/gsap
interface ScrollTriggerInstance extends gsap.plugins.ScrollTriggerInstance {
    refresh(safe?: boolean, force?: boolean);
}

interface TimelineInstance extends gsap.core.Timeline {
    scrollTrigger: ScrollTriggerInstance;
}

interface TweenInstance extends gsap.core.Tween {
    scrollTrigger: ScrollTriggerInstance;
}

export type Instance = TimelineInstance | TweenInstance;

interface BaseAnimation {
    instance: Instance;
    condition?: AnimationCondition;
    scrollTrigger: ScrollTriggerInstance;
    observe?: Element[]
}

interface TimelineAnimation extends BaseAnimation {
    instance: TimelineInstance;
    condition?: AnimationCondition;
    scrollTrigger: ScrollTriggerInstance;
}

interface TweenAnimation extends BaseAnimation {
    instance: TweenInstance;
    condition?: AnimationCondition;
    scrollTrigger: ScrollTriggerInstance;
}

type Animation = TimelineAnimation | TweenAnimation;

export interface GroupOptions extends TriggerOptions {
    condition?: AnimationCondition;
    observe?: Element[];
}

function isElement(element: any): element is Element {
    return element instanceof Node && ('tagName' in element);
}

export default class ScrollAnimations extends ThisBound {
    private readonly options: Required<Options>;

    private readonly bus = new Bus<{
        resize: [ScrollAnimations]
    }>();

    on = this.bus.on;
    off = this.bus.off;

    private readonly events = new Events;

    private readonly onObserverResizeThrottled = function_throttle_initial(10, this.onObserverResize);

    private readonly observer = new ResizeObserver(this.onObserverResizeThrottled);

    private animations: Map<TweenInstance | TimelineInstance, Animation> = new Map;

    constructor(options: Options = {}) {
        super();

        this.options = ScrollAnimations.resolveOptions(options);

        this.addEvents();
    }

    destroy() {
        this.removeEvents();

        this.animations.forEach(animation => {
            ScrollAnimations.disableAnimation(animation);

            animation.instance.kill();
        });

        this.observer.disconnect();
    }

    tween(target: Target, options: AnimationOptions, method: 'from'|'to') {
        const {scrollTrigger, condition, observe, ...tweenOptions} = options;

        const instance = gsap[method](target, {
            scrollTrigger: this.resolveScrollTriggerOptionsForTarget(target, scrollTrigger),
            ...this.options.tweenOptions,
            ...tweenOptions
        }) as TweenInstance;

        this.addAnimation(instance, condition, observe);

        return instance;
    }

    to(target: Target, options: AnimationOptions) {
        return this.tween(target, options, 'to');
    }

    from(target: Target, options: AnimationOptions) {
        return this.tween(target, options, 'from');
    }

    getScrollTriggerOptionsFromAttribute(target: Target): TriggerOptions {
        if (typeof target === 'string') {
            target = document.querySelector(target);
        }

        if (target instanceof HTMLElement) {
            const json = target.dataset.scrollTrigger;
            const options: TriggerOptions | undefined = json
                ? safe_call(`[ScrollAnimations]: Failed to parse options on target: ${json}`, () => JSON.parse(json))
                : undefined;

            if (options) {
                return options;
            }
        }

        return {};
    }

    resolveScrollTriggerOptionsForTarget(target: Target, merge?: TriggerOptions): TriggerOptions {
        merge = merge ?? {};

        return this.resolveTriggerOptions({
            trigger: target as TriggerTarget,
            ...merge,
            ...this.getScrollTriggerOptionsFromAttribute(target)
        });
    }

    fromTo(target: Target, from: gsap.TweenVars, to: AnimationOptions) {
        const {scrollTrigger, observe, condition, ...tweenOptions} = to;

        const instance = gsap.fromTo(target, from, {
            scrollTrigger: this.resolveScrollTriggerOptionsForTarget(target, scrollTrigger),
            ...this.options.tweenOptions,
            ...tweenOptions
        }) as TweenInstance;

        this.addAnimation(instance, condition, observe);

        return instance;
    }

    each(target: HTMLElement[]|string, executor: (animations: this, arg: HTMLElement, index: number) => any) {
        node_each(target, (node, index) => {
            executor(this, node, index);
        })
    }

    timeline(options: GroupOptions, timelineOpts: TimelineOptions = {}) {
        const {condition, observe, ...trigOpts} = options;

        const instance = gsap.timeline({
            ...timelineOpts,
            scrollTrigger: this.resolveTriggerOptions(trigOpts)
        }) as TimelineInstance;

        this.addAnimation(instance, condition, observe);

        return instance;
    }

    private addAnimation(instance: Instance, condition: AnimationCondition = undefined, observe: Element[] = []) {
        // Extract the scrollTrigger at this point as the type information has not been defined
        // for the presence of the scrollTrigger property on the Timeline instance object.
        const animation = {
            instance,
            condition,
            observe,
            scrollTrigger: instance.scrollTrigger
        } as Animation;

        this.animations.set(instance, animation);

        ScrollAnimations.updateAnimation(animation);

        observe.forEach(element => this.observer.observe(element));
    }

    group(options: GroupOptions) {
        return new Group(this, options);
    }

    resolveTriggerOptions<T extends TriggerOptions>(options: T): T {
        return {
            ...this.options.scrollTrigger,
            ...options
        }
    }

    createTrigger(options: TriggerOptions) {
        return ScrollTrigger.create(
            this.resolveTriggerOptions(options)
        );
    }

    resize(safe = false) {
        this.animations.forEach(animation => {
            ScrollAnimations.updateAnimation(animation);

            animation.scrollTrigger.refresh(safe);
        });

        this.bus.emit('resize', this);
    }

    private addEvents() {
        this.events.on(window, 'resize', () => this.resize());

        this.options.observe.forEach(element => this.observer.observe(element));
    }

    private removeEvents() {
        this.events.offAll();

        this.options.observe.forEach(element => this.observer.unobserve(element));
    }

    private onObserverResize(entries: ResizeObserverEntry[]) {
        const elements = entries.map(entry => entry.target);

        if (array_includes_any(elements, this.options.observe)) {
            this.resize();
        } else {
            this.animations.forEach(animation => {
                if (animation.observe && array_includes_any(elements, animation.observe)) {
                    animation.scrollTrigger.refresh();
                }
            });
        }
    }

    private static resolveOptions(options: Options): Required<Options> {
        return {
            scrollTrigger: {
                start: 'top bottom',
                end: 'bottom top',
                scrub: 0.4,
                invalidateOnRefresh: true
            },
            tweenOptions: {
                ease: 'none'
            },
            observe: [document.body],
            ...options
        }
    }

    private static updateAnimation(animation: Animation) {
        if (ScrollAnimations.evaluateCondition(animation)) {
            ScrollAnimations.enableAnimation(animation);
        } else {
            ScrollAnimations.disableAnimation(animation);
        }
    }

    private static enableAnimation(animation: Animation) {
        this.getAnimationTargets(animation).forEach(target => {
            if (isElement(target)) {
                target.classList.add('is-animation-enabled');
            }
        });

        // Due to some unusual behaviour when enabling/disabling tweens, we need to clear props before we go ahead
        // with enabling it, otherwise the initial state of the scroll animation at position 0 will be the state
        // that the element was left in when the scrollTrigger was paused.
        ScrollAnimations.clearProps(animation);

        // Invalidate cached properties
        animation.instance.invalidate();

        if (this.isTimelineAnimation(animation)) {
            animation.instance.getChildren().forEach(child => child.invalidate());
        }

        animation.scrollTrigger.enable();
    }

    private static disableAnimation(animation: Animation) {
        this.getAnimationTargets(animation).forEach(target => {
            if (isElement(target)) {
                target.classList.remove('is-animation-enabled');
            }
        });

        animation.scrollTrigger.disable(true, false);

        // Clear props so no transforms/scales are left applied in the disabled state
        ScrollAnimations.clearProps(animation);
    }

    private static clearProps(animation: Animation) {
        gsap.set(this.getAnimationTargets(animation), {
            clearProps: 'all'
        });
    }

    private static getAnimationTargets(animation: Animation): gsap.TweenTarget[] {
        if (this.isTweenAnimation(animation)) {
            return animation.instance.targets();
        } else {
            return animation.instance.getChildren().reduce((a, b) => a.concat(b), []);
        }
    }

    private static evaluateCondition(animation: Animation) {
        const {condition} = animation;

        switch (typeof condition) {
            case 'string':
                return window.matchMedia(condition).matches;

            case 'function':
                return condition(animation);
        }

        return true;
    }

    private static isTweenAnimation(value: Animation): value is TweenAnimation {
        return value.instance instanceof TweenLite;
    }

    private static isTimelineAnimation(value: Animation): value is TimelineAnimation {
        return value.instance instanceof TimelineLite;
    }
}